(() => {
  const q = window.makeStorefrontQuery;
  if (!q) {
    console.error('[Storefront] storefront-client.js must be loaded before storefront-queries');
    return;
  }

  const TTL_2M = 2 * 60 * 1000;

  async function productsByIds(productIds, { variantCount = 30 } = {}) {
    const ids = (productIds || []).map((id) => window.toGID(id));
    if (!ids.length) return [];

    const query = `
      query ProductsByIds($ids: [ID!]!, $variantCount: Int!) {
        nodes(ids: $ids) {
          __typename
          ... on Product {
            id
            handle
            title
            availableForSale
            featuredImage { url altText }
            priceRange {
              minVariantPrice { amount currencyCode }
              maxVariantPrice { amount currencyCode }
            }
            variants(first: $variantCount) {
              nodes {
                id
                title
                availableForSale
                selectedOptions { name value }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }
        }
      }`;

    const data = await q(query, { ids, variantCount }, { cacheTTL: TTL_2M });

    return (data?.nodes || [])
      .filter((n) => n && n.__typename === 'Product')
      .map((p) => {
        const variants = p.variants?.nodes || [];
        const price = p.priceRange?.minVariantPrice?.amount ?? null;
        const compareAtMin = variants
          .map((v) => v.compareAtPrice?.amount)
          .filter(Boolean)
          .map(Number);
        const compare_at_price = compareAtMin.length ? String(Math.min(...compareAtMin)) : null;

        return {
          id: p.id,
          handle: p.handle,
          title: p.title,
          price, // string amount
          compare_at_price, // string amount or null
          featured_image: p.featuredImage?.url || null,
          variants: variants.map((v) => ({
            id: v.id,
            title: v.title,
            available: v.availableForSale,
            price: v.price?.amount ?? null,
            compare_at_price: v.compareAtPrice?.amount ?? null,
            selectedOptions: v.selectedOptions || [],
          })),
          available: p.availableForSale,
        };
      });
  }

  async function getProductMetafieldReferences(idOrGid, namespace, key, queryName, { first = 20 } = {}) {
    const id = window.toGID(idOrGid);
    const query = `
      query ${queryName}($id: ID!, $first: Int!) {
        product(id: $id) {
          metafield(namespace: "${namespace}", key: "${key}") {
            value
            references(first: $first) {
              nodes {
                ... on Product {
                  id
                  handle
                  title
                  variants(first: 1) { nodes { id availableForSale } }
                }
              }
            }
          }
        }
      }`;
    return await q(query, { id, first }, { cacheTTL: TTL_2M });
  }

  const STARTER_METAFIELD_CONFIG = [
    { key: 'starter_multivitamin', queryName: 'StarterMultivitaminReferences' },
    { key: 'starter_biotic', queryName: 'StarterBioticReferences' },
    { key: 'starter_magnesium', queryName: 'StarterMagnesiumReferences' },
  ];

  function firstVariantIdFromReferences(nodes) {
    if (!Array.isArray(nodes)) {
      return null;
    }

    for (const node of nodes) {
      const variantId = node?.variants?.nodes?.find(Boolean)?.id;
      if (variantId) {
        return variantId;
      }
    }

    return null;
  }

  async function productStarterMetafieldVariantByKey(idOrGid, { key, queryName }, options = {}) {

    const data = await getProductMetafieldReferences(idOrGid, 'custom', key, queryName, options);

    const metafield = data?.product?.metafield;

    if (!metafield) {
      return null;
    }

    if (metafield.value != null && metafield.value !== '') {
      const stringValue = String(metafield.value);
      return stringValue;
    }

    return null;
  }

  function makeStarterMetafieldFetcher(config) {
    return async function productStarterVariantById(idOrGid, options = {}) {
      return await productStarterMetafieldVariantByKey(idOrGid, config, options);
    };
  }

  const productStarterMultivitaminVariantById = makeStarterMetafieldFetcher(STARTER_METAFIELD_CONFIG[0]);
  const productStarterBioticVariantById = makeStarterMetafieldFetcher(STARTER_METAFIELD_CONFIG[1]);
  const productStarterMagnesiumVariantById = makeStarterMetafieldFetcher(STARTER_METAFIELD_CONFIG[2]);

  async function productStarterMetafieldsById(idOrGid, options = {}) {

    const [multivitamin, biotic, magnesium] = await Promise.all([
      productStarterMultivitaminVariantById(idOrGid, options),
      productStarterBioticVariantById(idOrGid, options),
      productStarterMagnesiumVariantById(idOrGid, options),
    ]);



    const result = [
      ['starter_multivitamin', multivitamin],
      ['starter_biotic', biotic],
      ['starter_magnesium', magnesium],
    ].reduce((acc, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    return result;
  }

  async function productSidecartUpsellsById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'custom', 'sidecart_upsell_products', 'SidecartUpsells', options);
    return data?.product?.metafield?.references?.nodes ?? [];
  }

  async function productReviewsById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'judgeme', 'badge', 'ProductReviews', options);
    return data?.product?.metafield?.value || null;
  }

  async function productBlurbById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'custom', 'blurb', 'ProductBlurb', options);
    return data?.product?.metafield?.value || null;
  }

  async function productSubscriptionVariantById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'custom', 'subscription_variant', 'SubscriptionVariant', options);
    const variantId = data?.product?.metafield?.value;
    console.log('🔍 [SUBSCRIPTION_VARIANT] Retrieved variant ID:', variantId);
    return variantId;
  }

  async function productHideSubscribeButtonById(idOrGid, options = {}) {
    const data = await getProductMetafieldReferences(idOrGid, 'custom', 'hide_subscribe_and_save_button_from_minicart', 'HideSubscribeButton', options);
    const value = data?.product?.metafield?.value;
    console.log("shouldHideButton ", data);
    // Return true if the metafield is "true" (string) or true (boolean), otherwise false
    return value === 'true' || value === true;
  }

  async function productAddOnPricingById(idOrGid) {
    const id = window.toGID(idOrGid);
    const query = `
      query ProductAddOnPricing($id: ID!) {
        product(id: $id) {
          promo: metafield(namespace: "custom", key: "bfcm_add_on_promo_price") {
            value
          }
          full: metafield(namespace: "custom", key: "bfcm_add_on_full_price") {
            value
          }
        }
      }`;

    const data = await q(query, { id }, { cacheTTL: TTL_2M });
    const product = data?.product;

    return {
      promo: product?.promo?.value ?? null,
      full: product?.full?.value ?? null
    };
  }

  window.Storefront = {
    productsByIds,
    productSidecartUpsellsById,
    productReviewsById,
    productBlurbById,
    productSubscriptionVariantById,
    productStarterMultivitaminVariantById,
    productStarterBioticVariantById,
    productStarterMagnesiumVariantById,
    productStarterMetafieldsById,
    productHideSubscribeButtonById,
    productAddOnPricingById,
  };

  q.productsByIds = productsByIds;
  q.productSidecartUpsellsById = productSidecartUpsellsById;
  q.productReviewsById = productReviewsById;
  q.productBlurbById = productBlurbById;
  q.productSubscriptionVariantById = productSubscriptionVariantById;
  q.productStarterMultivitaminVariantById = productStarterMultivitaminVariantById;
  q.productStarterBioticVariantById = productStarterBioticVariantById;
  q.productStarterMagnesiumVariantById = productStarterMagnesiumVariantById;
  q.productStarterMetafieldsById = productStarterMetafieldsById;
  q.productAddOnPricingById = productAddOnPricingById;
})();
